<template>
  <b-modal
    id="student-email-modal"
    :visible="isEmailModalOpen"
    centered
    scrollable
    :size="hasThread ? 'xl' : 'lg'"
    header-class="align-items-start"
    body-class="p-0"
    footer-class="py-75"
    ok-only
    ok-title="Close"
    @hidden="closeModal"
    @hide="$emit('hide')"
  >
    <!-- Header: direction, subject, timestamp -->
    <template #modal-header="{ close }">
      <div class="d-flex align-items-center">
        <b-avatar rounded size="42" :variant="directionVariant" class="mr-1">
          <feather-icon :icon="directionIcon" size="20" />
        </b-avatar>
        <div>
          <h4 class="mb-25 font-weight-bolder">{{ active.subject || '(No subject)' }}</h4>
          <div class="d-flex align-items-center">
            <b-badge pill :variant="directionVariant" class="mr-75 text-capitalize">{{ directionLabel }}</b-badge>
            <small v-if="headerTime" class="text-muted">{{ headerTime }}</small>
          </div>
        </div>
      </div>
      <b-button variant="flat-secondary" class="btn-icon" size="sm" @click="close()">
        <feather-icon icon="XIcon" size="18" />
      </b-button>
    </template>

    <b-row no-gutters>
      <!-- Thread navigator -->
      <b-col v-if="hasThread" cols="12" md="4" lg="3" class="email-thread border-right">
        <div class="email-thread__header">
          <span>Conversation</span>
          <b-badge variant="light-secondary" pill>{{ thread.length }}</b-badge>
        </div>
        <div v-if="loadingThread" class="text-center py-2">
          <b-spinner small />
        </div>
        <button
          v-for="(mail, i) in thread"
          :key="mail.id || i"
          type="button"
          class="email-thread__item"
          :class="{ 'email-thread__item--active': mail.id === active.id }"
          @click="selectEmail(mail)"
        >
          <b-avatar
            size="36"
            :variant="mail.direction === 'outbound' ? 'light-info' : 'light-success'"
            :text="senderInitials(mail)"
            class="mr-75 flex-shrink-0"
          />
          <div class="min-w-0 flex-grow-1">
            <div class="d-flex align-items-center justify-content-between">
              <span class="email-thread__name text-truncate mr-50">{{ senderName(mail) }}</span>
              <small class="email-thread__time flex-shrink-0 text-nowrap">{{ relativeTime(mail) }}</small>
            </div>
            <div class="email-thread__snippet">{{ snippet(mail) }}</div>
            <div v-if="mail.attachments && mail.attachments.length" class="email-thread__badge">
              <feather-icon icon="PaperclipIcon" size="11" class="mr-25" />{{ mail.attachments.length }}
            </div>
          </div>
        </button>
      </b-col>

      <!-- Detail pane -->
      <b-col cols="12" :md="hasThread ? 8 : 12" :lg="hasThread ? 9 : 12" class="email-detail">
        <div class="email-meta rounded border mb-1">
          <div class="email-meta__row">
            <span class="email-meta__label">From</span>
            <span class="email-meta__value">{{ senderDisplay || '—' }}</span>
          </div>
          <div class="email-meta__row">
            <span class="email-meta__label">To</span>
            <span class="email-meta__value">{{ recipientDisplay || '—' }}</span>
          </div>
          <div v-if="ccDisplay" class="email-meta__row">
            <span class="email-meta__label">Cc</span>
            <span class="email-meta__value">{{ ccDisplay }}</span>
          </div>
        </div>

        <!-- Attachments -->
        <div v-if="attachments.length" class="d-flex flex-wrap align-items-center mb-1">
          <button
            v-for="(attachment, index) in attachments"
            :key="index"
            type="button"
            class="email-attachment mr-50 mb-50"
            :disabled="downloadingIndex === index"
            @click="downloadAttachment(index)"
          >
            <feather-icon :icon="downloadingIndex === index ? 'LoaderIcon' : 'PaperclipIcon'" size="14" class="mr-25" />
            <span class="text-truncate">{{ attachment.filename }}</span>
            <small v-if="attachment.size" class="ml-25 text-muted">{{ formatBytes(attachment.size) }}</small>
          </button>
        </div>

        <!-- Body toggle -->
        <div v-if="hasHtml && hasPlain" class="d-flex align-items-center justify-content-end mb-50">
          <span class="mr-50 small text-muted">Rich HTML</span>
          <b-form-checkbox v-model="emailAsHtml" switch />
        </div>

        <!-- Body -->
        <b-form-textarea v-if="!showHtml" class="email-plain" disabled :value="active.body && active.body.plain" rows="8" max-rows="20" />
        <!--
          Received email bodies are untrusted HTML from external senders. Render them
          DOMPurify-sanitized inside a sandboxed iframe (no allow-scripts / allow-same-origin)
          so any residual markup cannot execute or reach the app's session/DOM.
        -->
        <iframe
          v-else
          class="email-html-frame"
          :srcdoc="emailHtmlSafe"
          :style="{ height: frameHeight }"
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          referrerpolicy="no-referrer"
          title="Email content"
          @load="onFrameLoad"
        ></iframe>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { BModal, BButton, BBadge, BAvatar, BRow, BCol, BSpinner, BFormCheckbox, BFormTextarea } from 'bootstrap-vue';
import { computed, ref } from '@vue/composition-api';
import moment from 'moment';
import Ripple from 'vue-ripple-directive';
import DOMPurify from 'dompurify';
import store from '@/store';

export default {
  components: {
    BModal,
    BButton,
    BBadge,
    BAvatar,
    BRow,
    BCol,
    BSpinner,
    BFormCheckbox,
    BFormTextarea,
  },
  directives: {
    Ripple,
  },
  props: {
    studentData: { type: Object, required: true },
    emailId: { type: String, required: true },
    emailData: { type: Object, required: true },
    isEmailModalOpen: { type: Boolean, required: true },
  },
  methods: {
    closeModal() {
      this.$emit('hide');
    },
  },
  setup(props) {
    // The active email drives the detail pane; it starts as the opened one and changes as
    // the user clicks through the thread.
    const activeEmail = ref(props.emailData);
    const thread = ref([props.emailData]);
    const loadingThread = ref(false);

    const active = computed(() => activeEmail.value || {});
    const hasThread = computed(() => thread.value.length > 1);

    const formatRecipient = (r) => {
      if (!r) return '';
      return r.name ? `${r.name} <${r.email}>` : r.email;
    };
    const senderName = (mail) => (mail.sender && (mail.sender.name || mail.sender.email)) || 'Unknown';
    const senderInitials = (mail) => {
      const words = senderName(mail).split(/\s+/).filter(Boolean);
      return words
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    };
    const snippet = (mail) => {
      const body = mail.body || {};
      let text = (body.plain || '').trim();
      // Fall back to stripped HTML when there's no plaintext part.
      if (!text && body.html) text = body.html.replace(/<[^>]+>/g, ' ').replace(/&[a-z]+;/gi, ' ');
      // Trim quoted history and common signatures so the preview shows the new content only.
      text = text
        .split(/^-{2,}\s*Original Message\s*-{2,}/im)[0]
        .split(/^\s*On .+wrote:/im)[0]
        .split(/^Sent from /im)[0]
        .split(/^\s*>/m)[0]
        .replace(/\s+/g, ' ')
        .trim();
      if (!text) return '(no preview)';
      return text.length > 100 ? `${text.slice(0, 100)}…` : text;
    };
    const displayTime = (value) => {
      if (!value) return '';
      const m = moment(value);
      return m.isValid() ? m.format('MMM D, YYYY h:mm A') : String(value);
    };
    const relativeTime = (mail) => {
      const m = moment(mail.receivedAt || mail.createdAt);
      return m.isValid() ? m.fromNow() : '';
    };

    // Direction (of the active email)
    const isOutbound = computed(() => active.value.direction === 'outbound');
    const directionLabel = computed(() => (isOutbound.value ? 'Sent' : 'Received'));
    const directionVariant = computed(() => (isOutbound.value ? 'light-info' : 'light-success'));
    const directionIcon = computed(() => (isOutbound.value ? 'ArrowUpRightIcon' : 'ArrowDownLeftIcon'));
    const headerTime = computed(() => displayTime(active.value.receivedAt || active.value.createdAt));

    // Participants
    const senderDisplay = computed(() => formatRecipient(active.value.sender));
    const recipientDisplay = computed(() => ((active.value.recipients || {}).to || []).map(formatRecipient).join(', '));
    const ccDisplay = computed(() => ((active.value.recipients || {}).cc || []).map(formatRecipient).join(', '));

    // Body view
    const hasHtml = computed(() => !!(active.value.body && active.value.body.html));
    const hasPlain = computed(() => !!(active.value.body && active.value.body.plain));
    const emailAsHtml = ref(hasHtml.value);
    const showHtml = computed(() => emailAsHtml.value && hasHtml.value);
    const emailHtmlSafe = computed(() => {
      const raw = (active.value.body && active.value.body.html) || '';
      const clean = DOMPurify.sanitize(raw, { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] });
      return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;margin:12px;color:#1a1a1a;background:#fff;overflow-wrap:break-word;line-height:1.5}img{max-width:100%;height:auto}a{color:#0d6efd}</style></head><body>${clean}</body></html>`;
    });

    // Size the (sandboxed, script-less) iframe to its content so short emails don't leave a
    // huge white void; cap it so a giant email scrolls internally instead of exploding the modal.
    const frameHeight = ref('160px');
    const onFrameLoad = (event) => {
      try {
        const doc = event.target.contentDocument;
        const height = Math.min(Math.max(doc.body.scrollHeight + 24, 120), 800);
        frameHeight.value = `${height}px`;
      } catch (err) {
        frameHeight.value = '480px';
      }
    };

    const selectEmail = (mail) => {
      activeEmail.value = mail;
      emailAsHtml.value = !!(mail.body && mail.body.html);
      frameHeight.value = '160px'; // reset; @load recomputes for the new body
    };

    // Load the rest of the thread (chronological) so the user can page through it.
    const loadThread = async () => {
      const threadId = props.emailData && props.emailData.threadId;
      const studentId = props.studentData && props.studentData.id;
      if (!threadId || !studentId) return;

      loadingThread.value = true;
      try {
        const { data } = await store.dispatch('studentStoreModule/fetchStudentEmails', {
          id: studentId,
          queryParams: { threadId, limit: 100, sortBy: 'createdAt:asc' },
        });
        if (data && Array.isArray(data.results) && data.results.length) {
          thread.value = data.results;
          activeEmail.value = data.results.find((e) => e.id === props.emailData.id) || props.emailData;
        }
      } catch (err) {
        // Keep the single opened email if the thread can't be loaded.
      } finally {
        loadingThread.value = false;
      }
    };
    loadThread();

    // Attachments
    const attachments = computed(() => active.value.attachments || []);
    const downloadingIndex = ref(null);
    const downloadAttachment = async (index) => {
      if (downloadingIndex.value !== null) return;
      downloadingIndex.value = index;
      try {
        const { data } = await store.dispatch('studentStoreModule/downloadEmailAttachment', {
          id: active.value.id,
          index,
        });
        if (data && data.url) window.open(data.url, '_blank', 'noopener');
      } finally {
        downloadingIndex.value = null;
      }
    };

    const formatBytes = (bytes) => {
      if (!bytes) return '';
      const units = ['B', 'KB', 'MB', 'GB'];
      let value = bytes;
      let unit = 0;
      while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
      }
      return `${value.toFixed(unit ? 1 : 0)} ${units[unit]}`;
    };

    return {
      active,
      thread,
      hasThread,
      loadingThread,
      selectEmail,
      senderName,
      senderInitials,
      snippet,
      relativeTime,
      emailAsHtml,
      showHtml,
      hasHtml,
      hasPlain,
      emailHtmlSafe,
      frameHeight,
      onFrameLoad,
      senderDisplay,
      recipientDisplay,
      ccDisplay,
      attachments,
      downloadingIndex,
      downloadAttachment,
      directionLabel,
      directionVariant,
      directionIcon,
      headerTime,
      formatBytes,
    };
  },
};
</script>

<style lang="scss" scoped>
.email-thread {
  // Stretches to match the detail pane height (flex row) so the panel never leaves a void.
  max-height: 70vh;
  overflow-y: auto;
  // Subtle inset so the sidebar reads as its own panel in both light and dark themes.
  background-color: rgba(#22292f, 0.03);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.6rem 0.85rem;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b9b9c3;
    border-bottom: 1px solid rgba(#b8c2cc, 0.2);
    position: sticky;
    top: 0;
    background-color: inherit;
    backdrop-filter: blur(2px);
    z-index: 1;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    width: 100%;
    text-align: left;
    padding: 0.75rem 0.85rem;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(#b8c2cc, 0.14);
    color: inherit; // buttons don't inherit the theme text color by default
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(#b8c2cc, 0.1);
    }

    &--active {
      background-color: rgba(#7367f0, 0.14);
      box-shadow: inset 3px 0 0 #7367f0;
    }
  }

  &__name {
    font-weight: 600;
    font-size: 0.9rem;
  }

  &__time {
    font-size: 0.7rem;
    color: #b9b9c3;
  }

  &__snippet {
    font-size: 0.8rem;
    line-height: 1.3;
    color: #b9b9c3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    margin-top: 0.3rem;
    padding: 0.05rem 0.4rem;
    font-size: 0.7rem;
    color: #b9b9c3;
    border: 1px solid rgba(#b8c2cc, 0.35);
    border-radius: 1rem;
  }
}

.email-detail {
  padding: 1rem 1.25rem;
}

.text-nowrap {
  white-space: nowrap;
}

.min-w-0 {
  min-width: 0;
}

.email-meta {
  &__row {
    display: flex;
    padding: 0.5rem 0.85rem;
    font-size: 0.9rem;

    & + & {
      border-top: 1px solid rgba(#b8c2cc, 0.25);
    }
  }

  &__label {
    flex: 0 0 3rem;
    color: #b9b9c3;
    font-weight: 500;
  }

  &__value {
    flex: 1;
    word-break: break-word;
  }
}

.email-attachment {
  display: inline-flex;
  align-items: center;
  max-width: 16rem;
  padding: 0.3rem 0.6rem;
  border: 1px solid rgba(#b8c2cc, 0.4);
  border-radius: 0.357rem;
  font-size: 0.85rem;
  color: inherit;
  background: transparent;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--primary, #7367f0);
    background-color: rgba(#7367f0, 0.08);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.email-plain {
  font-family: inherit;
  // Cap the plaintext body and let it scroll internally instead of stretching the modal.
  max-height: 55vh;
  overflow-y: auto !important;
}

.email-html-frame {
  width: 100%;
  border: 1px solid rgba(#b8c2cc, 0.4);
  border-radius: 0.357rem;
  background-color: #fff;
  transition: height 0.15s ease;
  // Cap the rendered frame (overrides the inline height set on load) so a tall email
  // scrolls inside the frame rather than making the whole modal huge.
  max-height: 55vh;
}
</style>
